import {create} from 'zustand';

import {KrasLayerInfo} from '@/models/dto/system/kras-layer-info';

export type KrasLayerGroup = {
  layerCode: string;
  layerName: string;
  items: KrasLayerInfo[];
};

interface KrasLayerStoreState {
  krasLayers: KrasLayerInfo[];
  groupedLayers: KrasLayerGroup[];
  selectedCodes: Set<string>;
  setKrasLayers: (layers: KrasLayerInfo[]) => void;
  toggleLayerCode: (code: string) => void;
  setLayerCodes: (codes: string[]) => void;
  isLayerCodeSelected: (code: string) => boolean;
  getSelectedCodesByLayerCode: (layerCode: string) => string[];
  getLayerStyleByCode: (code: string) => KrasLayerInfo | undefined;
  getWMSLayerId: (layerCode: string) => string;
  clear: () => void;
}

const groupByLayerCode = (layers: KrasLayerInfo[]): KrasLayerGroup[] => {
  const grouped = new Map<string, KrasLayerGroup>();

  for (const item of layers) {
    const key = item.layerCode;
    if (!grouped.has(key)) {
      grouped.set(key, {
        layerCode: item.layerCode,
        layerName: item.layerName,
        items: []
      });
    }
    grouped.get(key)!.items.push(item);
  }

  return Array.from(grouped.values());
};

export const useKrasLayerStore = create<KrasLayerStoreState>((set, get) => ({
  krasLayers: [],
  groupedLayers: [],
  selectedCodes: new Set(),

  setKrasLayers: (layers) =>
    set({
      krasLayers: layers,
      groupedLayers: groupByLayerCode(layers)
    }),

  toggleLayerCode: (code) =>
    set((state) => {
      const updated = new Set(state.selectedCodes);
      updated.has(code) ? updated.delete(code) : updated.add(code);
      return {selectedCodes: updated};
    }),

  setLayerCodes: (codes) => {
    set({selectedCodes: new Set(codes)});
  },

  isLayerCodeSelected: (code) => get().selectedCodes.has(code),

  getSelectedCodesByLayerCode: (layerCode) => {
    const group = get().groupedLayers.find((g) => g.layerCode === layerCode);
    if (!group) return [];
    const selected = get().selectedCodes;
    return group.items.map((item) => item.code).filter((code) => selected.has(code));
  },

  getLayerStyleByCode: (code) => get().krasLayers.find((layer) => layer.code === code),

  getWMSLayerId: (layerCode: string) => `wms-kras-${layerCode}`,

  clear: () => set({selectedCodes: new Set()})
}));
